import styled from '@emotion/styled'
import React, { useMemo } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import IssuesGrid from '../../components/entities/IssuesGrid'
import useFetchEntitiesWithCache from '../../hooks/useFetchEntitiesWithCache'

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import {
    Box,
    CardContent,
    Card as MuiCard,
    Divider as MuiDivider,
    Tab,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { spacing } from '@mui/system'
import { useTranslation } from 'react-i18next'
import IssueFilters from './filters/IssueFilters'
import { categorizeIssues } from '../../components/utils/categorizeIssues'
import { useAtom } from 'jotai'
import { issueActiveTabAtom, issueFiltersAtom, issueGridCurrentPageAtom } from '../../atoms/issueAtom'
import LoadingIndicator from '../../components/LoadingIndicator'
import { useLogUserEvent } from '../../hooks/useLogUserEvent'
import { useFilterIssues } from './utils'
import { IssueEntity } from 'types/IssueEntity'

const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

function NoIssues() {
    return (
        <Card mb={6}>
            <CardContent>
                <Typography variant='body2' gutterBottom>
                    Inga emissioner hittades
                </Typography>
            </CardContent>
        </Card>
    )
}

function PreviousIssuesGrid({ issues }: { issues: IssueEntity[] }) {
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    if (issues?.length === 0) {
        return <NoIssues />
    }

    const fields = [
        'issueLabel',
        // 'issueType',
        // 'shareName',
        // 'tags',
        // 'shareSubPrice',
        // 'sharePrice',
        // 'subPriceDiscount',
        // 'priceChange',
        'date',
        'subscribedSize',
        // 'financialAdvisors',
        // 'outcome',
    ]

    if (!isSmallScreen) {
        fields.push(...['priceChange', 'financialAdvisors', 'outcome'])
    }
    return (
        <IssuesGrid
            fields={fields}
            issues={issues}
            hasSearchFilter
            sortModel={[{ field: 'date', sort: 'desc' }]}
            isCompact={isSmallScreen}
        />
    )
}

function CurrentIssuesGrid({ issues }: { issues: IssueEntity[] }) {
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    if (issues?.length === 0) {
        return <NoIssues />
    }

    const fields = [
        'issueLabel',
        // 'issueType',
        // 'shareName',
        // 'tags',
        // 'shareSubPrice',
        // 'sharePrice',
        // 'subPriceDiscount',
        'date',
        // 'preMoneyValuation',
        'offeredSize',
        // 'terms',
    ]

    if (!isSmallScreen) {
        fields.push(...['financialAdvisors', 'totalCommitment'])
    }

    return (
        <IssuesGrid
            fields={fields}
            issues={issues}
            sortModel={[{ field: 'date', sort: 'desc' }]}
            isCompact={isSmallScreen}
        />
    )
}

function FutureIssuesGrid({ issues }: { issues: IssueEntity[] }) {
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    if (issues?.length === 0) {
        return <NoIssues />
    }

    const fields = [
        'issueLabel',
        // 'issueType',
        // 'shareName',
        // 'tags',
        // 'shareSubPrice',
        // 'sharePrice',
        // 'subPriceDiscount',
        'date',
        // 'preMoneyValuation',
        'offeredSize',
        // 'terms',
        // 'financialAdvisors',
        // 'totalCommitment',
    ]

    if (!isSmallScreen) {
        fields.push(...['financialAdvisors', 'totalCommitment'])
    }

    return (
        <IssuesGrid
            fields={fields}
            issues={issues}
            sortModel={[{ field: 'date', sort: 'desc' }]}
            isCompact={isSmallScreen}
        />
    )
}

function IssuesTabs({ issues }: { issues: IssueEntity[] }) {
    const { t } = useTranslation()
    const [tabValue, setTabValue] = useAtom(issueActiveTabAtom)
    const [, setIssueGridCurrentPage] = useAtom(issueGridCurrentPageAtom)

    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    const { previousIssues, currentIssues, futureIssues } = useMemo(() => categorizeIssues(issues), [issues])

    return (
        <Card mb={6}>
            <CardContent>
                <TabContext value={tabValue}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList
                            onChange={(e, newValue) => {
                                setIssueGridCurrentPage(0)
                                setTabValue(newValue)
                            }}
                        >
                            <Tab value='current' label={`${t('site.issues.current')} (${currentIssues.length})`} />
                            <Tab value='future' label={`${t('site.issues.future')} (${futureIssues.length})`} />
                            <Tab value='previous' label={`${t('site.issues.previous')} (${previousIssues.length})`} />
                        </TabList>
                    </Box>
                    <TabPanel value='current' sx={isSmallScreen ? { padding: '12px 0px' } : null}>
                        <CurrentIssuesGrid issues={currentIssues} />
                    </TabPanel>
                    <TabPanel value='future' sx={isSmallScreen ? { padding: '12px 0px' } : null}>
                        <FutureIssuesGrid issues={futureIssues} />
                    </TabPanel>
                    <TabPanel value='previous' sx={isSmallScreen ? { padding: '12px 0px' } : null}>
                        <PreviousIssuesGrid issues={previousIssues} />
                    </TabPanel>
                </TabContext>
            </CardContent>
        </Card>
    )
}

function IssuesView() {
    const { t } = useTranslation()

    const [filters] = useAtom(issueFiltersAtom)
    const { isLoading, result: fetchedIssues } = useFetchEntitiesWithCache('issue')
    const filteredIssues = useFilterIssues(fetchedIssues || [], filters)
    useLogUserEvent('issue')

    return (
        <React.Fragment>
            <Helmet title={t('entities.issue.plural')} />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'block',
                }}
            >
                {t('entities.issue.plural')}
            </Typography>
            <Divider my={6} />
            <Box>
                <IssueFilters isLoading={isLoading} issues={fetchedIssues} />
                <LoadingIndicator
                    loading={isLoading}
                    label='Laddar emissionsdata...'
                    labelSx={{
                        color: 'primary.main',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        textAlign: 'center',
                    }}
                />
                {!isLoading && <IssuesTabs issues={filteredIssues} />}
            </Box>
        </React.Fragment>
    )
}

export default IssuesView
